import type { ReactNode } from 'react';
import { startTransition, useOptimistic } from 'react';

import { useSearchParams } from 'react-router';
import { addStringIfNotExists } from '@/shared/lib/array/addStringIfNotExists';
import { splitCommaSeparatedKeys } from '@/shared/lib/format/splitCommaSeparatedKeys';

import {
  ADDITIONAL_TABS_PARAM_NAME,
  FACTSHEET_PARAM_NAME,
  FACTSHEET_TAB_PARAM_NAME,
  TAB_NAMES
} from '../constants';
import { FactsheetOpenStateContext } from './FactsheetOpenStateContextBase';

interface FactsheetOpenStateProviderProps {
  children: ReactNode;
}

/**
 * Opening/closing a factsheet still updates the URL (shareable link, browser back
 * button), but that navigation re-renders everything subscribed to the location —
 * including heavy pages like the catalog table — before the dialog/drawer could
 * even mount. useOptimistic shows the open/close instantly while the URL update
 * settles in the background as a transition, so the two are no longer coupled.
 */
export const FactsheetOpenStateProvider = ({ children }: FactsheetOpenStateProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCode = searchParams.get(FACTSHEET_PARAM_NAME);
  const urlAdditionalTabs = splitCommaSeparatedKeys(
    searchParams.get(ADDITIONAL_TABS_PARAM_NAME) ?? undefined
  );
  const [code, setOptimisticCode] = useOptimistic(
    urlCode,
    (_current: string | null, next: string | null) => next
  );
  const [additionalTabs, setOptimisticAdditionalTabs] = useOptimistic(
    urlAdditionalTabs,
    (_current: string[], next: string[]) => next
  );

  const open = (newCode: string) => {
    startTransition(() => {
      // Nothing open yet: this becomes the main factsheet, same as before.
      if (code === null) {
        setOptimisticCode(newCode);
        setSearchParams(prev => {
          prev.set(FACTSHEET_PARAM_NAME, newCode);
          return prev;
        });
        return;
      }

      // A factsheet is already open: push onto the additional tabs instead of
      // replacing it, so opening another index from the catalog adds a tab
      // rather than discarding whatever is already open. The optimistic update
      // shows the new tab immediately instead of waiting for the URL update to
      // settle.
      if (newCode === code) return;

      const updatedTabs = addStringIfNotExists(additionalTabs, newCode);
      setOptimisticAdditionalTabs(updatedTabs);
      setSearchParams(prev => {
        prev.set(ADDITIONAL_TABS_PARAM_NAME, updatedTabs.join(','));
        return prev;
      });
    });
  };

  const close = () => {
    startTransition(() => {
      setOptimisticCode(null);
      setOptimisticAdditionalTabs([]);
      setSearchParams(prev => {
        prev.delete(FACTSHEET_TAB_PARAM_NAME);
        prev.delete(FACTSHEET_PARAM_NAME);
        prev.delete(ADDITIONAL_TABS_PARAM_NAME);
        return prev;
      });
    });
  };

  // Replaces the whole set of open tabs at once (e.g. closing the main tab
  // promotes whatever was next to main) so the main "indexInformation" param
  // and the additional-tabs param are always updated together and never drift
  // apart.
  const setOpenTabs = (tabs: string[]) => {
    startTransition(() => {
      const [newCode = null, ...newAdditionalTabs] = tabs;
      setOptimisticCode(newCode);
      setOptimisticAdditionalTabs(newAdditionalTabs);
      setSearchParams(prev => {
        if (newCode) {
          prev.set(FACTSHEET_PARAM_NAME, newCode);
        } else {
          prev.delete(FACTSHEET_PARAM_NAME);
        }
        if (newAdditionalTabs.length > 0) {
          prev.set(ADDITIONAL_TABS_PARAM_NAME, newAdditionalTabs.join(','));
        } else {
          prev.delete(ADDITIONAL_TABS_PARAM_NAME);
        }
        if (prev.get(FACTSHEET_TAB_PARAM_NAME) !== TAB_NAMES.FLOWCHART) {
          prev.delete(FACTSHEET_TAB_PARAM_NAME);
        }
        return prev;
      });
    });
  };

  return (
    <FactsheetOpenStateContext value={{ code, additionalTabs, open, close, setOpenTabs }}>
      {children}
    </FactsheetOpenStateContext>
  );
};
